import { createAsyncThunk } from '@reduxjs/toolkit';
import { helpdeskSettingsApi } from '../../../api/services/helpdesk-settings.service';
import type {
  HelpdeskSettings,
  RoutingRule,
  SLAPolicy,
  OperatorScheduleEntry,
  TicketType,
  TicketStatusDef,
  ScheduleException,
  WorkShift,
} from '../../../types/helpdeskSettingsInterfaces';

export const fetchHelpdeskSettings = createAsyncThunk<
  HelpdeskSettings,
  void,
  { rejectValue: string }
>('helpdeskSettings/fetchSettings', async (_, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.getSettings();
    // Singleton — API returns array with one item via ViewSet.list
    const data = Array.isArray(response.data) ? response.data[0] : response.data;
    return data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка загрузки настроек');
  }
});

export const updateHelpdeskSettings = createAsyncThunk<
  HelpdeskSettings,
  Partial<HelpdeskSettings>,
  { rejectValue: string }
>('helpdeskSettings/updateSettings', async (data, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.updateSettings(data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка сохранения настроек');
  }
});

// --- Routing Rules ---
export const fetchRoutingRules = createAsyncThunk<
  RoutingRule[],
  void,
  { rejectValue: string }
>('helpdeskSettings/fetchRoutingRules', async (_, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.getRoutingRules();
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка загрузки правил');
  }
});

export const createRoutingRule = createAsyncThunk<
  RoutingRule,
  Partial<RoutingRule>,
  { rejectValue: string }
>('helpdeskSettings/createRoutingRule', async (data, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.createRoutingRule(data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка создания правила');
  }
});

export const updateRoutingRule = createAsyncThunk<
  RoutingRule,
  { id: number; data: Partial<RoutingRule> },
  { rejectValue: string }
>('helpdeskSettings/updateRoutingRule', async ({ id, data }, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.updateRoutingRule(id, data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка обновления правила');
  }
});

export const deleteRoutingRule = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('helpdeskSettings/deleteRoutingRule', async (id, thunkApi) => {
  try {
    await helpdeskSettingsApi.deleteRoutingRule(id);
    return id;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка удаления правила');
  }
});

export const reorderRoutingRules = createAsyncThunk<
  number[],
  number[],
  { rejectValue: string }
>('helpdeskSettings/reorderRoutingRules', async (order, thunkApi) => {
  try {
    await helpdeskSettingsApi.reorderRoutingRules(order);
    return order;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка сортировки');
  }
});

// --- Work Shifts (рабочие смены) ---
export const fetchWorkShifts = createAsyncThunk<
  WorkShift[],
  void,
  { rejectValue: string }
>('helpdeskSettings/fetchWorkShifts', async (_, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.getWorkShifts();
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка загрузки смен');
  }
});

export const createWorkShift = createAsyncThunk<
  WorkShift,
  Partial<WorkShift>,
  { rejectValue: string }
>('helpdeskSettings/createWorkShift', async (data, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.createWorkShift(data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка создания смены');
  }
});

export const updateWorkShift = createAsyncThunk<
  WorkShift,
  { id: number; data: Partial<WorkShift> },
  { rejectValue: string }
>('helpdeskSettings/updateWorkShift', async ({ id, data }, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.updateWorkShift(id, data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка обновления смены');
  }
});

export const deleteWorkShift = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('helpdeskSettings/deleteWorkShift', async (id, thunkApi) => {
  try {
    await helpdeskSettingsApi.deleteWorkShift(id);
    return id;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка удаления смены');
  }
});

// --- Schedule Exceptions (исключения по датам) ---
export const fetchScheduleExceptions = createAsyncThunk<
  ScheduleException[],
  void,
  { rejectValue: string }
>('helpdeskSettings/fetchScheduleExceptions', async (_, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.getScheduleExceptions();
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка загрузки исключений');
  }
});

export const createScheduleException = createAsyncThunk<
  ScheduleException,
  Partial<ScheduleException>,
  { rejectValue: string }
>('helpdeskSettings/createScheduleException', async (data, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.createScheduleException(data);
    return response.data;
  } catch (e: any) {
    const d = e.response?.data;
    return thunkApi.rejectWithValue(
      d?.non_field_errors?.[0] || d?.detail || 'Ошибка создания исключения',
    );
  }
});

export const deleteScheduleException = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('helpdeskSettings/deleteScheduleException', async (id, thunkApi) => {
  try {
    await helpdeskSettingsApi.deleteScheduleException(id);
    return id;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка удаления исключения');
  }
});

// --- Ticket Types ---
export const fetchTicketTypes = createAsyncThunk<
  TicketType[],
  void,
  { rejectValue: string }
>('helpdeskSettings/fetchTicketTypes', async (_, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.getTicketTypes();
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка загрузки типов заявок');
  }
});

export const createTicketType = createAsyncThunk<
  TicketType,
  Partial<TicketType>,
  { rejectValue: string }
>('helpdeskSettings/createTicketType', async (data, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.createTicketType(data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(
      e.response?.data?.name?.[0] || e.response?.data?.detail || 'Ошибка создания типа заявки',
    );
  }
});

export const updateTicketType = createAsyncThunk<
  TicketType,
  { id: number; data: Partial<TicketType> },
  { rejectValue: string }
>('helpdeskSettings/updateTicketType', async ({ id, data }, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.updateTicketType(id, data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(
      e.response?.data?.name?.[0] || e.response?.data?.detail || 'Ошибка обновления типа заявки',
    );
  }
});

export const deleteTicketType = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('helpdeskSettings/deleteTicketType', async (id, thunkApi) => {
  try {
    await helpdeskSettingsApi.deleteTicketType(id);
    return id;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка удаления типа заявки');
  }
});

// --- Ticket Statuses ---
export const fetchTicketStatuses = createAsyncThunk<
  TicketStatusDef[],
  void,
  { rejectValue: string }
>('helpdeskSettings/fetchTicketStatuses', async (_, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.getTicketStatuses();
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка загрузки статусов');
  }
});

export const createTicketStatus = createAsyncThunk<
  TicketStatusDef,
  Partial<TicketStatusDef>,
  { rejectValue: string }
>('helpdeskSettings/createTicketStatus', async (data, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.createTicketStatus(data);
    return response.data;
  } catch (e: any) {
    const d = e.response?.data;
    return thunkApi.rejectWithValue(
      d?.code?.[0] || d?.name?.[0] || d?.detail || 'Ошибка создания статуса',
    );
  }
});

export const updateTicketStatus = createAsyncThunk<
  TicketStatusDef,
  { id: number; data: Partial<TicketStatusDef> },
  { rejectValue: string }
>('helpdeskSettings/updateTicketStatus', async ({ id, data }, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.updateTicketStatus(id, data);
    return response.data;
  } catch (e: any) {
    const d = e.response?.data;
    return thunkApi.rejectWithValue(
      d?.code?.[0] || d?.is_active?.[0] || d?.detail || 'Ошибка обновления статуса',
    );
  }
});

export const deleteTicketStatus = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('helpdeskSettings/deleteTicketStatus', async (id, thunkApi) => {
  try {
    await helpdeskSettingsApi.deleteTicketStatus(id);
    return id;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка удаления статуса');
  }
});

// --- SLA Policies ---
export const fetchSLAPolicies = createAsyncThunk<
  SLAPolicy[],
  void,
  { rejectValue: string }
>('helpdeskSettings/fetchSLAPolicies', async (_, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.getSLAPolicies();
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка загрузки SLA');
  }
});

export const createSLAPolicy = createAsyncThunk<
  SLAPolicy,
  Partial<SLAPolicy>,
  { rejectValue: string }
>('helpdeskSettings/createSLAPolicy', async (data, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.createSLAPolicy(data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка создания SLA');
  }
});

export const updateSLAPolicy = createAsyncThunk<
  SLAPolicy,
  { id: number; data: Partial<SLAPolicy> },
  { rejectValue: string }
>('helpdeskSettings/updateSLAPolicy', async ({ id, data }, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.updateSLAPolicy(id, data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка обновления SLA');
  }
});

export const deleteSLAPolicy = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('helpdeskSettings/deleteSLAPolicy', async (id, thunkApi) => {
  try {
    await helpdeskSettingsApi.deleteSLAPolicy(id);
    return id;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка удаления SLA');
  }
});

// --- Schedules ---
export const fetchSchedules = createAsyncThunk<
  OperatorScheduleEntry[],
  number | undefined,
  { rejectValue: string }
>('helpdeskSettings/fetchSchedules', async (operatorId, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.getSchedules(operatorId);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка загрузки расписаний');
  }
});

export const updateSchedule = createAsyncThunk<
  OperatorScheduleEntry,
  { id: number; data: Partial<OperatorScheduleEntry> },
  { rejectValue: string }
>('helpdeskSettings/updateSchedule', async ({ id, data }, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.updateSchedule(id, data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка обновления расписания');
  }
});

export const createSchedule = createAsyncThunk<
  OperatorScheduleEntry,
  Partial<OperatorScheduleEntry>,
  { rejectValue: string }
>('helpdeskSettings/createSchedule', async (data, thunkApi) => {
  try {
    const response = await helpdeskSettingsApi.createSchedule(data);
    return response.data;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка создания расписания');
  }
});

export const deleteSchedule = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('helpdeskSettings/deleteSchedule', async (id, thunkApi) => {
  try {
    await helpdeskSettingsApi.deleteSchedule(id);
    return id;
  } catch (e: any) {
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка удаления расписания');
  }
});

// Удаляет все записи расписания оператора (обычно 7 — по дням недели).
// Отдельный thunk, а не цикл dispatch(deleteSchedule) в компоненте, чтобы
// стор обновился одним fulfilled и не мигал промежуточными состояниями.
export const deleteOperatorSchedules = createAsyncThunk<
  number[],
  { operatorId: number; entryIds: number[] },
  { rejectValue: string }
>('helpdeskSettings/deleteOperatorSchedules', async ({ entryIds }, thunkApi) => {
  try {
    await Promise.all(entryIds.map((id) => helpdeskSettingsApi.deleteSchedule(id)));
    return entryIds;
  } catch (e: any) {
    // Часть запросов могла успеть удалиться — перезагружаем список,
    // чтобы стор не разошёлся с сервером.
    thunkApi.dispatch(fetchSchedules(undefined));
    return thunkApi.rejectWithValue(e.response?.data?.detail || 'Ошибка удаления расписания оператора');
  }
});
